import type { CSSProperties } from "react";

/**
 * How far a wrapped prose line's continuation sits in (`M7/E14`).
 *
 * A help page indents its prose with spaces — `      ` under a form, `  ! `
 * before a trap — and `white-space: pre-wrap` keeps those spaces on the first
 * line only. The second line of a long trap therefore started at column 0, under
 * the `!`, and read as a new paragraph. The indent is the leading spaces plus a
 * one-character marker and its space, so a continuation lines up with the text
 * rather than with the marker.
 */
export function hangingIndent(text: string): number {
  const match = /^( *)(?:([!\-·•*]) )?/.exec(text);
  const spaces = match?.[1]?.length ?? 0;
  return spaces + (match?.[2] ? 2 : 0);
}

/**
 * The inline style for one scrollback line, or none.
 *
 * A table row (`pre`) never wraps, so it needs no indent. For prose, the first
 * line is pulled back by the same amount the block is pushed in: it still starts
 * at column 0 with its own spaces, and every wrapped line starts at the indent.
 * `ch` is exact here because the terminal's font is monospace.
 */
export function lineStyle(line: { text: string; pre?: boolean }): CSSProperties | undefined {
  if (line.pre) return undefined;
  const indent = hangingIndent(line.text);
  return indent > 0 ? { paddingLeft: `${indent}ch`, textIndent: `-${indent}ch` } : undefined;
}

/** A scrollback line's class: its kind, and `pre` for a table row. */
export function lineClass(line: { kind: string; pre?: boolean }): string {
  return `line ${line.kind}${line.pre ? " pre" : ""}`;
}
